// Payment and refund dates, and This year / Last 30 days, use the firm time zone (America/New_York when unset). A timestamp is not shown as its UTC day. Delivered 2026-11-02.
// Payments table stays inside the white card: the navy header meets the card edges, and Refunds plus the Refund button fit. A narrower window scrolls the table inside the card. Delivered 2026-11-01.
// LawPay dashboard refunds/voids: status_label (Refunded, Partially refunded) on the status column, and each refund as its own line (amount, method, date, invoice it reverses). Absent fields keep Cleared / Processing / Reversed and a dash. Delivered 2026-11-01.
// pkg195 — payment method 'unknown' displays as Unknown — delivered 2026-10-31
// Owner refund on each payment row: amount defaults to the refundable remainder, reason required, confirm before POST /api/payments/:id/refund { amount, reason }. Refund rows show Pending / Completed / Failed. A manual payment says it is not sent to LawPay. Delivered 2026-11-01.
// pkg193 — Payments opens on All time: From/To stay empty so GET /api/payments is not windowed and every payment comes back. This year and Last 30 days still fill local calendar dates (never toISOString, which shifted a day west of UTC). Account column and All/Operating/Trust filter via ?deposit_account=; a missing deposit_account shows a dash — delivered 2026-10-31
// pkg176 — date display fix: DATE columns (payment_date, issue_date, due_date, link dates) were formatted through local time and showed the previous day west of UTC; now parsed as calendar dates, matching the invoices page's formatter — delivered 2026-10-04
// pkg174 — firm-wide Payments page (D-174d): every payment in a date window with LawPay provenance and processing state, plus a LawPay links panel (open / paid / canceled, resend + cancel). Reads only existing endpoints: GET /api/payments and GET /api/payment-requests (firm-wide, 90 days) — delivered 2026-10-03
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import RefundPaymentModal, { paymentCanBeRefunded, paymentRefunds, RefundStatusBadge } from '../components/RefundPaymentModal';
import { paymentMethodLabel } from '../utils/paymentMethodLabel';
import { paymentStatusLabel, paymentStatusTone, refundAmount, refundReversesLabel, refundWhen } from '../utils/paymentRefundDisplay';
import { firmRangeFor, formatFirmDate } from '../utils/firmDate';

const F = { fontFamily: 'Arial, sans-serif' };
const money = n => '$' + (+n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const fmtDate = (s) => formatFirmDate(s);
const th = { ...F, fontSize: 11, fontWeight: 700, color: '#fff', background: '#0F1838', padding: '8px 8px', textAlign: 'left', whiteSpace: 'nowrap' };
const td = { ...F, fontSize: 13, padding: '8px 8px', borderBottom: '1px solid #eee', verticalAlign: 'middle' };
const pill = (bg, fg, text) => <span style={{ ...F, fontSize: 10, fontWeight: 700, letterSpacing: 0.4, padding: '2px 7px', borderRadius: 9, background: bg, color: fg, whiteSpace: 'nowrap' }}>{text}</span>;

function rangeFor(preset) {
  return firmRangeFor(preset);
}
const PRESETS = [['this-year', 'This year'], ['30', 'Last 30 days'], ['all', 'All time']];

function accountBadge(account) {
  if (account === 'operating') return pill('#e8f1fb', '#1c4e80', 'Operating');
  if (account === 'trust') return pill('#fff8e1', '#8a6a00', 'Trust');
  return '—';
}
function rangeCaption(from, to) {
  if (!from && !to) return 'Showing all payments, with no date limit.';
  if (from && to) return `Showing ${fmtDate(from)} through ${fmtDate(to)}.`;
  if (from) return `Showing payments on or after ${fmtDate(from)}.`;
  return `Showing payments on or before ${fmtDate(to)}.`;
}

const STATUS_PILL = {
  partial: ['#e8f1fb', '#1c4e80'],
  refunded: ['#f0f0f0', '#555'],
  voided: ['#f3f3f3', '#666'],
  reversed: ['#fdecea', '#8a1c1c'],
  failed: ['#fdecea', '#8a1c1c'],
  pending: ['#fff3e0', '#9a4b00'],
  cleared: ['#e4f3e8', '#1e6b35'],
};
function payStatus(p) {
  const label = paymentStatusLabel(p);
  if (label) {
    const [bg, fg] = STATUS_PILL[paymentStatusTone(label, p.status)] || STATUS_PILL.cleared;
    return pill(bg, fg, label);
  }
  if (p.status === 'reversed') return pill('#fdecea', '#8a1c1c', 'Reversed');
  if (p.status === 'pending') return pill('#fff3e0', '#9a4b00', p.source === 'lawpay' ? 'Processing' : 'Pending');
  if (p.status === 'refunded') return pill('#f0f0f0', '#555', 'Refunded');
  if (p.status === 'voided') return pill('#f3f3f3', '#666', 'Voided');
  return pill('#e4f3e8', '#1e6b35', 'Cleared');
}
function RefundLine({ refund: r }) {
  const amount = refundAmount(r);
  const method = paymentMethodLabel(r.method, String(r.method || '').replace(/_/g, ' ')) || '—';
  const when = refundWhen(r);
  const reverses = refundReversesLabel(r);
  return (
    <div style={{ fontSize: 12, lineHeight: 1.35 }}>
      <div>{amount == null ? '—' : money(amount)} · {method} · {when ? fmtDate(when) : '—'}</div>
      <div style={{ color: '#555' }}>{reverses || '—'}</div>
      {r.status ? <div style={{ marginTop: 2 }}><RefundStatusBadge status={r.status} /></div> : null}
    </div>
  );
}
function linkStatus(r) {
  if (r.status === 'paid') return pill('#e4f3e8', '#1e6b35', 'Paid');
  if (r.status === 'canceled') return pill('#eee', '#555', 'Canceled');
  return pill('#e8f1fb', '#1c4e80', 'Open');
}

export default function AdminPaymentsPage() {
  const navigate = useNavigate();
  const { adminUser } = useAdminAuth();
  const isOwner = adminUser?.role === 'owner';
  const [refundTarget, setRefundTarget] = useState(null);
  const [notice, setNotice] = useState('');
  const [preset, setPreset] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [status, setStatus] = useState('');
  const [account, setAccount] = useState('');
  const [payments, setPayments] = useState([]);
  const [links, setLinks] = useState([]);
  const [lawpay, setLawpay] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [tab, setTab] = useState('payments');

  const load = useCallback(async () => {
    setErr('');
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (status) params.status = status;
      if (account) params.deposit_account = account;
      const [p, l] = await Promise.all([adminApi.listPayments(params), adminApi.paymentRequests({})]);
      setPayments(p || []); setLinks(l || []);
    } catch (e) { setErr(e.message); }
  }, [from, to, status, account]);
  useEffect(() => { load(); adminApi.lawpayStatus().then(setLawpay).catch(() => setLawpay(null)); }, [load]);

  function applyPreset(id) {
    const r = rangeFor(id);
    setPreset(id);
    setFrom(r.from);
    setTo(r.to);
  }

  async function resend(r) {
    setBusy(r.id);
    try { await adminApi.resendPaymentRequest(r.id); await load(); } catch (e) { setErr(e.message); } finally { setBusy(''); }
  }
  async function cancel(r) {
    if (!window.confirm(`Cancel link ${r.reference}? The client's existing email link will still open on LawPay; a payment made there will still be recorded.`)) return;
    setBusy(r.id);
    try { await adminApi.cancelPaymentRequest(r.id); await load(); } catch (e) { setErr(e.message); } finally { setBusy(''); }
  }

  // Narrow again locally so the filter still holds if the server has not started honoring deposit_account.
  const visible = payments.filter(p => !account || p.deposit_account === account);
  const total = visible.filter(p => p.status !== 'reversed').reduce((s, p) => s + (+p.amount || 0), 0);
  const viaLawpay = visible.filter(p => p.source === 'lawpay' && p.status !== 'reversed').reduce((s, p) => s + (+p.amount || 0), 0);
  const openLinks = links.filter(l => l.status === 'open');
  const dateInvalid = from && to && from > to;

  return (
    <div>
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 26, margin: '0 0 4px' }}>Payments</h1>
      <p style={{ ...F, fontSize: 13, color: '#666', marginTop: 0 }}>
        Every payment recorded for the firm — manual entries and LawPay — plus the LawPay links you've sent.
        The list opens on all payments. Use This year, Last 30 days, or the dates to narrow it.
        {lawpay && lawpay.connected && lawpay.mode === 'test' && <strong> LawPay is in TEST MODE.</strong>}
        {lawpay && !lawpay.connected && <> LawPay isn't connected — <button className="btn btn-sm" style={{ marginLeft: 6 }} onClick={() => navigate('/admin/dashboard/firm-settings')}>connect it in Firm Settings</button></>}
      </p>
      {err && <div className="alert alert-error">{err}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, margin: '12px 0 16px' }}>
        {[['Received (window)', money(total)], ['Via LawPay', money(viaLawpay)], ['Processing', visible.filter(p => p.status === 'pending').length], ['Open LawPay links', openLinks.length]].map(([l, v]) => (
          <div key={l} className="card" style={{ padding: 12 }}>
            <div style={{ ...F, fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{l}</div>
            <div style={{ ...F, fontSize: 20, fontWeight: 700, marginTop: 2 }}>{v}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {['payments', 'links'].map(t => <button key={t} className={`btn btn-sm ${tab === t ? 'btn-primary' : ''}`} onClick={() => setTab(t)}>{t === 'payments' ? 'Payments' : `LawPay links (${links.length})`}</button>)}
      </div>

      {tab === 'payments' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '20px 20px 12px' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ ...F, fontSize: 12, color: '#666' }}>Range</span>
            {PRESETS.map(([id, label]) => (
              <button key={id} type="button" className={`btn btn-sm ${preset === id ? 'btn-primary' : ''}`} onClick={() => applyPreset(id)}>{label}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 4 }}>
            <div className="form-group" style={{ margin: 0 }}><label>From</label><input type="date" value={from} onChange={e => { setPreset('custom'); setFrom(e.target.value); }} /></div>
            <div className="form-group" style={{ margin: 0 }}><label>To</label><input type="date" value={to} onChange={e => { setPreset('custom'); setTo(e.target.value); }} /></div>
            <div className="form-group" style={{ margin: 0 }}><label>Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)}><option value="">All</option><option value="cleared">Cleared</option><option value="pending">Pending / Processing</option><option value="reversed">Reversed</option></select></div>
            <div className="form-group" style={{ margin: 0 }}><label>Account</label>
              <select value={account} onChange={e => setAccount(e.target.value)}><option value="">All</option><option value="operating">Operating</option><option value="trust">Trust</option></select></div>
          </div>
          <p style={{ ...F, fontSize: 12, color: '#888', margin: '0 0 10px' }}>{rangeCaption(from, to)}</p>
          {dateInvalid && <p style={{ ...F, fontSize: 12, color: '#B85042', margin: '-4px 0 10px' }}>The From date is after the To date.</p>}
          </div>
          {visible.length === 0 ? <p style={{ ...F, fontSize: 13, color: '#888', margin: '0 20px 20px' }}>{account ? `No ${account === 'trust' ? 'trust' : 'operating'} payments in this window.` : 'No payments in this window.'}</p> : (
            <div className="table-wrap">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Date</th><th style={th}>Client</th><th style={th}>Amount</th><th style={th}>Method</th><th style={th}>Account</th><th style={th}>Source</th><th style={th}>Status</th><th style={th}>Applied</th><th style={th}>Refunds</th>{isOwner && <th style={th} />}</tr></thead>
                <tbody>
                  {visible.map(p => (
                    <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/clients/${p.client_id}?tab=billing`)}>
                      <td style={td}>{fmtDate(p.payment_date)}</td>
                      <td style={td}>{p.client_name || '—'}</td>
                      <td style={{ ...td, fontFamily: 'monospace' }}>{money(p.amount)}</td>
                      <td style={td}>{paymentMethodLabel(p.method, String(p.method || '').replace('_', ' ')) || '—'}</td>
                      <td style={td}>{accountBadge(p.deposit_account)}</td>
                      <td style={td}>{p.source === 'lawpay' ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', maxWidth: '100%' }}><img src="/lawpay-logo.svg" alt="LawPay" style={{ height: 11 }} />{p.lawpay_status ? <span style={{ color: '#888', fontSize: 11 }}>{p.lawpay_status}</span> : null}</span> : 'Manual'}</td>
                      <td style={td}>{payStatus(p)}</td>
                      <td style={{ ...td, fontFamily: 'monospace' }}>{money(p.applied_total)}</td>
                      <td style={td}>
                        {paymentRefunds(p).length === 0 ? '—' : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {paymentRefunds(p).map((r, i) => (
                              <RefundLine key={r.id != null ? r.id : i} refund={r} />
                            ))}
                          </div>
                        )}
                      </td>
                      {isOwner && (
                        <td style={{ ...td, whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                          {paymentCanBeRefunded(p) && (
                            <button type="button" className="btn btn-sm" style={{ borderColor: '#b7870a', color: '#b7870a' }}
                              onClick={() => { setNotice(''); setRefundTarget(p); }}>
                              Refund
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'links' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {links.length === 0 ? <p style={{ ...F, fontSize: 13, color: '#888', margin: 0, padding: 20 }}>No LawPay links sent in the last 90 days. Send one from an invoice (Send LawPay Link) or a client's Billing tab (Request Retainer via LawPay).</p> : (
            <div className="table-wrap">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Sent</th><th style={th}>Client</th><th style={th}>For</th><th style={th}>Amount</th><th style={th}>Reference</th><th style={th}>To</th><th style={th}>Status</th><th style={th} /></tr></thead>
                <tbody>
                  {links.map(r => (
                    <tr key={r.id}>
                      <td style={td}>{fmtDate(r.last_sent_at || r.created_at)}{r.sent_count > 1 ? <span style={{ color: '#888', fontSize: 11 }}> ×{r.sent_count}</span> : null}</td>
                      <td style={td}><a href={`/admin/clients/${r.client_id}?tab=billing`} onClick={e => { e.preventDefault(); navigate(`/admin/clients/${r.client_id}?tab=billing`); }}>{r.client_name || '—'}</a></td>
                      <td style={td}>{r.purpose === 'retainer' ? 'Retainer (trust)' : `Invoice #${r.invoice_number}`}</td>
                      <td style={{ ...td, fontFamily: 'monospace' }}>{money(r.amount)}</td>
                      <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{r.reference}</td>
                      <td style={{ ...td, fontSize: 12 }}>{r.to_email || '—'}</td>
                      <td style={td}>{linkStatus(r)}{r.paid_at ? <span style={{ color: '#888', fontSize: 11 }}> {fmtDate(r.paid_at)}</span> : null}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {r.status === 'open' && <>
                          <button className="btn btn-sm" disabled={busy === r.id} onClick={() => resend(r)}>Resend</button>{' '}
                          <button className="btn btn-sm" disabled={busy === r.id} onClick={() => navigator.clipboard && navigator.clipboard.writeText(r.url)}>Copy link</button>{' '}
                          <button className="btn btn-sm" disabled={busy === r.id} onClick={() => cancel(r)} style={{ color: '#B85042' }}>Cancel</button>
                        </>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {refundTarget && (
        <RefundPaymentModal
          payment={refundTarget}
          onClose={() => setRefundTarget(null)}
          onRefunded={(text) => { setRefundTarget(null); setNotice(text); load(); }}
        />
      )}
    </div>
  );
}
